import React from 'react';
import autobind from 'autobind';
import { observer, inject } from 'mobx-react';
import { observable, makeObservable } from 'mobx';

import { Nullable } from 'chaika-frontend-types/backend';

import { AuthUser } from '@store/AuthUser/AuthUser';
import { authUserService, defaultOrganizationOption } from '@store/AuthUser/services/authUserService';
import { OrganizationList } from '@organization/store/organization/OrganizationList';

import { UserProfile, UserProfileProps } from '@core/Page/LeftMenu/UserProfile/UserProfile';
import { translate } from '@chaika/i18n/helper';

interface Props extends UserProfileProps {}

interface StoreProps {
    authUser: AuthUser;
    organizationList: OrganizationList;
}

const injectableStores: (keyof StoreProps)[] = [AuthUser.Name, OrganizationList.Name];

@observer
class Component extends React.Component<Props & StoreProps> {
    @observable
    private selectOrganizationOpen = false;

    constructor(props: Props & StoreProps) {
        super(props);
        makeObservable(this);
    }

    public render(): React.ReactNode {
        const { authUser, organizationList, isOpen } = this.props;
        const organization = authUser.organizationId ? organizationList.get(authUser.organizationId) : undefined;

        return React.createElement(UserProfile, {
            isOpen,
            hasOrganizations: authUser.hasRootAccess || authUser.organizationIds.length > 0,
            selectOrganizationOpen: this.selectOrganizationOpen,
            firstName: authUser.firstName,
            lastName: authUser.lastName,
            avatar: authUser.avatar,
            avatarUnits: authUser.avatarUnits,
            organization: organization?.name ?? translate('selectOrganization'),
            abbreviation: organization?.abbreviation ?? translate('select'),
            onOrganizationClick: this.handleOnOrganizationClick,
            onOrganizationSelect: this.handleOrganizationSelect,
        });
    }

    @autobind
    protected handleOnOrganizationClick() {
        this.selectOrganizationOpen = true;
    }

    @autobind
    protected async handleOrganizationSelect(selectedId?: Nullable<string | object>): Promise<void> {
        if (selectedId === defaultOrganizationOption.value) {
            await authUserService.updateSelectedOrganization();
        } else if (selectedId && typeof selectedId === 'string') {
            await authUserService.updateSelectedOrganization(selectedId);
        }

        this.selectOrganizationOpen = false;
    }
}

export const UserProfileContainer = inject<Props, StoreProps>(...injectableStores)(Component);
